import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import Keycloak from 'keycloak-js';
import { Observable, catchError, throwError } from 'rxjs';

/** True once a redirect to Keycloak login has been kicked off, so a burst of requests
 *  failing at once (common right after the session actually dies -- several in-flight
 *  calls all come back 401 together) doesn't call keycloak.login() more than once. The
 *  page navigates away as soon as the first call fires, so this only needs to survive
 *  for the lifetime of that one navigation. */
let redirectingToLogin = false;

/** Catches 401s that reach the backend despite includeBearerTokenInterceptor's own
 *  per-request token-refresh attempt (keycloak.config.ts's dusCondition/ekcCondition/etc.) --
 *  this only happens once the Keycloak client's own `authenticated` flag has already gone
 *  false, e.g. the configured inactivity timeout (keycloak.config.ts's
 *  `onInactivityTimeout: 'logout'`, 15 minutes) fired, or the underlying SSO session expired
 *  server-side. Previously this left the user looking at a bare failed network request with
 *  no explanation (e.g. "started a new onboarding session" silently 401ing) -- redirects
 *  through Keycloak login instead, back to the same URL, so a session-bound flow like the
 *  Bulk File Onboarding wizard's own resumable `:sessionId` route lands right back where it
 *  was rather than losing all progress. */
export const sessionExpiredInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const keycloak = inject(Keycloak);

    return next(req).pipe(
        catchError((error: unknown) => {
            if (error instanceof HttpErrorResponse && error.status === 401 && !redirectingToLogin) {
                redirectingToLogin = true;
                keycloak.login({ redirectUri: window.location.href });
            }
            return throwError(() => error);
        })
    );
};
